import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { ApiError } from '../types';

/**
 * Typed wrapper around useQuery for API calls
 */
export function useApiQuery<TData>(
  queryKey: string[],
  queryFn: () => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, AxiosError<ApiError>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<TData, AxiosError<ApiError>>({
    queryKey,
    queryFn,
    ...options,
  });
}

/**
 * Typed wrapper around useMutation for API calls
 */
export function useApiMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<UseMutationOptions<TData, AxiosError<ApiError>, TVariables>, 'mutationFn'>,
) {
  return useMutation<TData, AxiosError<ApiError>, TVariables>({
    mutationFn,
    ...options,
  });
}

/**
 * Hook to get the query client for cache invalidation
 */
export function useInvalidateQuery() {
  const queryClient = useQueryClient();

  return (queryKey: string[]) => {
    return queryClient.invalidateQueries({ queryKey });
  };
}
